import { faArrowLeft, faSignalBars } from "@fortawesome/pro-solid-svg-icons";
import {
  faArrowRightFromBracket,
  faBookBookmark,
} from "@fortawesome/sharp-solid-svg-icons";
import { Box, Stack } from "@mui/material";
import { useMemo } from "react";

import MarkColor from "@/assets/branding/mark_color.png";
import { Breadcrumb, Breadcrumbs, Icon, Typography } from "@/components/ui-v2";

import { PrimaryNavigationHeaderRow } from "../nav/components";
import { HelpLink, HelpLinkButton } from "./HelpLinkButton";

const statusLink: HelpLink = {
  name: "Status",
  href: "https://status.layer.ai/",
  description: "Check out our status page for any incidents.",
  icon: <Icon icon={faSignalBars} />,
};

const guidesLink: HelpLink = {
  name: "Guides",
  href: "https://help.layer.ai/en/",
  description: "Guides that cover popular requests.",
  icon: <Icon icon={faBookBookmark} />,
};

const logoutLink: HelpLink = {
  name: "Logout",
  href: "/logout",
  description:
    "Logging out and back in may help if you're repeatedly facing this issue.",
  icon: <Icon icon={faArrowRightFromBracket} />,
};

export interface ErrorMessageProps {
  title: string;
  description: string;
  showBackButton?: boolean;
  showHelpLinks?: boolean; // TODO: remove this and make it more granular, like showLogout but with showStatus, showGuides
  showLogout?: boolean;
  errorData?: Error;
}

export const ErrorMessage = (props: ErrorMessageProps) => {
  const {
    title,
    description,
    showBackButton,
    showHelpLinks,
    showLogout,
    errorData,
  } = props;

  const links = useMemo(() => {
    if (showHelpLinks) {
      return [statusLink, guidesLink, logoutLink];
    } else if (showLogout) {
      return [logoutLink];
    }
    return [];
  }, [showHelpLinks, showLogout]);

  return (
    <Stack sx={{ width: "100%", height: "100%" }} direction="column">
      {showBackButton && (
        <PrimaryNavigationHeaderRow>
          <Breadcrumbs>
            <Breadcrumb
              label="Back to Home"
              icon={<Icon icon={faArrowLeft} />}
              href="/"
            />
          </Breadcrumbs>
        </PrimaryNavigationHeaderRow>
      )}
      <Stack
        direction="column"
        spacing={4}
        sx={{
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
          padding: 4,
        }}
      >
        <a href="/">
          <img
            style={{ width: "auto", height: "64px" }}
            src={MarkColor}
            alt="Layer"
          />
        </a>
        <Typography variant="title2" fontWeight={700} textAlign="center">
          {title}
        </Typography>
        <Typography
          variant="large"
          color="secondary.2"
          textAlign="center"
          sx={{
            pt: 4,
            maxWidth: "42rem",
          }}
        >
          {description}
        </Typography>
        {errorData ? (
          <Box
            sx={{
              padding: 4,
              width: "100%",
            }}
          >
            <Box
              component="pre"
              sx={{
                fontSize: 12,
                color: "secondary.2",
                backgroundColor: "secondary.7",
                overflowY: "auto",
                padding: 4,
                borderRadius: 4,
              }}
            >
              {errorData.stack ?? Error.prototype.toString.call(errorData)}
            </Box>
          </Box>
        ) : null}
        {links.length > 0 && (
          <Stack
            direction="column"
            spacing={2}
            sx={{
              pt: 8,
            }}
          >
            {links.map((link) => (
              <HelpLinkButton key={link.name} link={link} />
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
